import React, {useContext} from 'react';
import {Table, MandarkTable, LoadingSpinner, Text, Anchor} from '@albert-io/atomic';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import {resource} from '@albert-io/json-api-framework/request/builder';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';

import PeopleContext from '../people.context';
import classroomStore from '../../../ClassroomV2.store';

import AddCoteacher from './AddCoteacher/AddCoteacher.react';
import RemoveCoteacher from './RemoveCoteacher/RemoveCoteacher.react';
import {coteachersHelpArticleUrl} from './coteachers.utils';
import './coteachers.scss';

/**
 * @todo: Double check if help article should point to the same URL for both empty states below.
 */

const els = {
  NoAccess() {
    return (
      <Text as='p' className='u-max-width_text co-teachers-message' paragraph>
        You do not have permission to add co-teachers to this classroom. Learn more about inviting
        co-teachers in{' '}
        <Anchor href={coteachersHelpArticleUrl} target='_blank' rel='noopener noreferrer' as='a'>
          this help article
        </Anchor>
        .
      </Text>
    );
  },
  NoCoteachers() {
    const {queries} = useContext(PeopleContext);
    return (
      <>
        <Text as='p' paragraph>
          You don&apos;t have any co-teachers.{' '}
          <Anchor href={coteachersHelpArticleUrl} target='_blank' rel='noopener noreferrer' as='a'>
            Learn more about co-teachers.
          </Anchor>
        </Text>
        <Table.TitleBar title='Approved' buttonGroup={<AddCoteacher />} />
        <MandarkTable query={queries.coteachers} theme='list'>
          <MandarkTable.Column
            heading='Name'
            content={(teacher) =>
              `${teacher.getDisplayName()} ${
                teacher.getId() === masqueradeStore.getUserIdByMasqueradeState() ? '(You)' : ''
              }`
            }
            sortBy='first_name'
          />
          <MandarkTable.Column
            heading='Email'
            content={(teacher) => teacher.getEmail()}
            sortBy='email'
          />
        </MandarkTable>
      </>
    );
  },
  CoteachersTable() {
    const {queries} = useContext(PeopleContext);
    return (
      <MandarkTable query={queries.coteachers} theme='list'>
        <MandarkTable.Column
          heading='Name'
          content={(teacher) =>
            `${teacher.getDisplayName()} ${
              teacher.getId() === masqueradeStore.getUserIdByMasqueradeState() ? '(You)' : ''
            }`
          }
          sortBy='first_name'
        />
        <MandarkTable.Column
          heading='Email'
          content={(teacher) => teacher.getEmail()}
          sortBy='email'
        />
        <MandarkTable.Column
          isActionMenu
          align='right'
          className='column__remove-teacher'
          content={(teacher) => <RemoveCoteacher teacher={teacher} />}
        />
      </MandarkTable>
    );
  }
};

const getOwnSchoolPersonnelRecordsQuery = (schoolId, teacherId) =>
  resource('school_personnel_v1').mandarkEndpoint(['school_personnel_v1']).filter({
    school_v5: schoolId,
    teacher_v1: teacherId
  });

const Coteachers = () => {
  const {queries} = useContext(PeopleContext);
  const schoolId = classroomStore.getActiveClassroomSchool().getId();
  const teacherId = masqueradeStore.getUserIdByMasqueradeState();

  const {
    isReady,
    resources: [ownSchoolPersonnelRecords]
  } = useMandarkQueries([getOwnSchoolPersonnelRecordsQuery(schoolId, teacherId)]);

  const coteacherCount = queries.coteachers
    .getResourceMetadata()
    .getIn(['page', 'total_entries'], null);
  /**
   * `school_personnel_v1` is both the singular and plural form of this resource, so we need to ensure it's ready before calling `List()` (e.g. `some`) methods on the resource.
   */
  const isApproved =
    isReady && ownSchoolPersonnelRecords.some((record) => record.getStatus() === 'approved');

  const hasNoCoteachers = coteacherCount === 1;

  let Body;
  if (!isReady) {
    Body = LoadingSpinner;
  } else if (!isApproved) {
    Body = els.NoAccess;
  } else if (hasNoCoteachers) {
    Body = els.NoCoteachers;
  } else {
    Body = els.CoteachersTable;
  }

  return (
    <>
      {!hasNoCoteachers && (
        <Table.TitleBar title='Approved' buttonGroup={isReady && isApproved && <AddCoteacher />} />
      )}
      <Body />
    </>
  );
};

export default Coteachers;
